import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { FileIcon } from "@/assets/icons";
import { Accordion } from "@/components/ui/accordion.tsx";
import { MAX_RANKING_INPUT } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useDataset } from "@/hooks/map/useDataset";
import { RootState } from "@/store";
import { setSelectedDataset } from "@/store/slice/datasetSlice";
import { setLegendFilters } from "@/store/slice/mapSlice";
import { setShowTimeSeriesModal } from "@/store/slice/uiSlice";
import { SpatialAggregationType } from "@/types/common.ts";
import { Dataset, DatasetAttributeSchema, DatasetData } from "@/types/dataset";
import { getBeforeId } from "@/utils/map";

import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from "@hello-pangea/dnd";
import { twMerge } from "tailwind-merge";

import DatasetItem from "./DatasetItem";

const NoDataset = ({
  isValidationError = false
}: {
  isValidationError?: boolean;
}) => {
  return (
    <div
      className={twMerge(
        "flex flex-col gap-3 p-6 items-center",
        isValidationError &&
          "border-cinnabar border-[1px] p-4 rounded-xl mt-2 mb-1"
      )}
    >
      <FileIcon className="fill-light-gray" />
      <span className="text-[12px] text-grey font-sans">No datasets yet.</span>
    </div>
  );
};

type DatasetListProps = {
  onOpenDatasetTabularModal: () => void;
  isSpatialAnalysis?: boolean;
  isValidationError?: boolean;
  /** `false` leaves each row with its visibility toggle and style
   * accordion but no data-table, attribute-filter or remove action. */
  withRowActions?: boolean;
};

const DatasetList = ({
  onOpenDatasetTabularModal,
  isSpatialAnalysis = false,
  isValidationError = false,
  withRowActions = true
}: DatasetListProps) => {
  const dispatch = useDispatch();

  const {
    datasets,
    visualizationOrder = [],
    rawDatasets
  } = useSelector((state: RootState) => state.dataset);
  const { legendFilters } = useSelector((state: RootState) => state.map);

  const {
    handleToggleDatasetVisibility,
    handleRemoveDataset,
    handleReorderDataset,
    handleChangeSelectedCategoryDataset,
    handleChangeStrokeAttributeDataset,
    handleRankConfigChange
  } = useDataset();

  const { handleFilterLayerByLegend } = useMap();

  const [itemFocused, setItemFocused] = useState<Dataset | null>(null);

  const handleOnDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    handleReorderDataset(result.draggableId, result.destination.index);
  };

  const onRemoveDataset = (id: string, spatialType: SpatialAggregationType) => {
    handleRemoveDataset(id, spatialType);
  };

  const handleResetLegendFilter = (data: Dataset) => {
    dispatch(
      setLegendFilters({
        ...legendFilters,
        [data.data.id]: []
      })
    );
  };

  const handleOnSelectAttribute = (
    selectedAttributeName: string,
    data: Dataset,
    rawDataset: DatasetData,
    orders: { id: string; type: SpatialAggregationType }[],
    step: number = 10
  ) => {
    handleResetLegendFilter(data);
    const previousLayerId = getBeforeId(data.data.id, orders);
    handleFilterLayerByLegend(
      [],
      {
        ...data,
        selectedCategory:
          data.categories.find(
            (value) => value.attribute_name === selectedAttributeName
          ) ?? data.selectedCategory
      },
      rawDataset,
      previousLayerId
    );
    handleChangeSelectedCategoryDataset(
      data,
      selectedAttributeName,
      data.style.colorFamily,
      step
    );
  };

  const handleOnSelectStrokeAttribute = (
    selectedAttributeName: string,
    data: Dataset,
    rawDataset: DatasetData,
    orders: { id: string; type: SpatialAggregationType }[],
    step: number = 10
  ) => {
    handleResetLegendFilter(data);
    const previousLayerId = getBeforeId(data.data.id, orders);
    handleFilterLayerByLegend(
      [],
      {
        ...data,
        style: {
          ...data.style,
          stroke: {
            ...data.style.stroke,
            attribute_name: selectedAttributeName
          }
        }
      },
      rawDataset,
      previousLayerId
    );
    handleChangeStrokeAttributeDataset(
      data,
      selectedAttributeName,
      data.style.colorFamily,
      step
    );
  };

  // Rank config handlers
  const makeRankHandlers = (dataset: Dataset, maxRankingInput: number) => ({
    handleChangeRankChecked: (value: boolean) => {
      handleRankConfigChange(dataset.data.id, dataset.style, {
        isEnabled: value
      });
    },
    handleChangeMaxRankingInput: (value: number) => {
      if (value > maxRankingInput) value = maxRankingInput;
      if (value < 1) value = 1;
      handleRankConfigChange(dataset.data.id, dataset.style, {
        maxRanking: value
      });
    },
    handleChangeMaxRankingSlider: (value: number[]) => {
      const newValue = Math.max(1, value[0]);
      handleRankConfigChange(dataset.data.id, dataset.style, {
        maxRanking: newValue
      });
    },
    handleRankConfigChangeOnSelectLabel: (
      isEnabled: boolean,
      category: DatasetAttributeSchema
    ) => {
      handleRankConfigChange(dataset.data.id, dataset.style, {
        isEnabled,
        category
      });
    }
  });

  // This will show the datasets in the order defined by `visualizationOrder`, both the available and the deleted ones
  const orderedDatasets = visualizationOrder.map((id) => datasets[id]);

  return (
    <div>
      {orderedDatasets.length ? (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="document" type="group">
            {(provided) => (
              <Accordion type="single" collapsible className="w-full">
                <ul
                  className="flex flex-col gap-2 mt-3 overflow-auto custom-scrollbar"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {orderedDatasets.map((item, index) => {
                    const totalRows = rawDatasets?.[item.data.id]?.rows.length;
                    const maxRankingInput = Math.min(
                      totalRows,
                      MAX_RANKING_INPUT
                    );
                    const rankHandlers = makeRankHandlers(
                      item,
                      maxRankingInput
                    );

                    return (
                      <Draggable
                        key={item.data.id}
                        draggableId={item.data.id}
                        index={index}
                      >
                        {(provided) => (
                          <DatasetItem
                            dragProvided={provided}
                            data={item}
                            isFocused={item.data.id === itemFocused?.data.id}
                            tourId={index === 0 ? "dataset-card" : undefined}
                            onMouseEnter={() => setItemFocused(item)}
                            onMouseLeave={() => setItemFocused(null)}
                            onRemoveDataset={() => {
                              onRemoveDataset(
                                item.data.id,
                                item.properties?.spatialType
                              );
                              // Todo: Remove when integration timeseries
                              const remainingH3Datasets =
                                orderedDatasets.filter(
                                  (dataset) =>
                                    dataset.data.id !== item.data.id &&
                                    dataset.data.spatial_aggregation_type ===
                                      "H3"
                                ).length;
                              if (remainingH3Datasets === 0) {
                                dispatch(setShowTimeSeriesModal(false));
                              }
                              handleResetLegendFilter(item);
                            }}
                            onOpenDatasetTabularModal={() => {
                              onOpenDatasetTabularModal();
                              dispatch(setSelectedDataset(item));
                            }}
                            isSpatialAnalysis={isSpatialAnalysis}
                            withRowActions={withRowActions}
                            onToggleDatasetVisibility={(e) => {
                              e.stopPropagation();
                              handleToggleDatasetVisibility(
                                item.data.id,
                                item.style.labelConfig.show_label,
                                item.style.rankConfig?.isEnabled || false
                              );
                            }}
                            onSelectAttribute={(value) => {
                              handleOnSelectAttribute(
                                value,
                                item,
                                rawDatasets[item.data.id ?? ""],
                                orderedDatasets.map((v) => ({
                                  id: v.data.id,
                                  type: v.data.spatial_aggregation_type
                                })),
                                item.style.colorSteps
                              );
                            }}
                            onSelectStrokeAttribute={(value) => {
                              handleOnSelectStrokeAttribute(
                                value,
                                item,
                                rawDatasets[item.data.id ?? ""],
                                orderedDatasets.map((v) => ({
                                  id: v.data.id,
                                  type: v.data.spatial_aggregation_type
                                })),
                                item.style.colorSteps || 10
                              );
                            }}
                            onChangeColorStep={(step) =>
                              handleOnSelectAttribute(
                                item.selectedCategory.attribute_name,
                                item,
                                rawDatasets[item.data.id ?? ""],
                                orderedDatasets.map((v) => ({
                                  id: v.data.id,
                                  type: v.data.spatial_aggregation_type
                                })),
                                step
                              )
                            }
                            onChangeStrokeColorStep={(step) =>
                              handleChangeStrokeAttributeDataset(
                                item,
                                item.style.stroke?.attribute_name || "",
                                item.style.colorFamily,
                                step
                              )
                            }
                            showLabel={item?.style?.labelConfig?.show_label}
                            totalRows={totalRows}
                            {...rankHandlers}
                          />
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </ul>
              </Accordion>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <NoDataset isValidationError={isValidationError} />
      )}
    </div>
  );
};

export default DatasetList;
